/**
 * Inline SVG icon set (stroke icons, 24px grid, currentColor). Replaces the
 * emoji glyphs the UI used to use: emoji render differently per platform and
 * can't take the brand color.
 */
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Svg({ size = 18, children, ...rest }: IconProps & { children: React.ReactNode }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const BrushIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M18.4 2.6a2 2 0 0 1 2.9 2.9L12 14.8l-2.9-2.9z" />
    <path d="M9.1 11.9 7 14a3 3 0 0 0-.9 2.1c0 1.6-1.3 2.9-3.1 2.9 1.2 1.6 3 2.4 5 2.4a5 5 0 0 0 5-5l-1-1.6" />
  </Svg>
);
export const EraserIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="m7 21-4.3-4.3a1.5 1.5 0 0 1 0-2.1L13.4 3.9a1.5 1.5 0 0 1 2.1 0l5.6 5.6a1.5 1.5 0 0 1 0 2.1L11.3 21" />
    <path d="M22 21H7" />
    <path d="m5 11 9 9" />
  </Svg>
);
export const HandIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M18 11V6a2 2 0 0 0-4 0v5" />
    <path d="M14 10V4a2 2 0 0 0-4 0v6" />
    <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
    <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.9-6-2.4l-3.6-3.6a2 2 0 0 1 2.8-2.8L7 15" />
  </Svg>
);
export const UndoIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
  </Svg>
);
export const RedoIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="m15 14 5-5-5-5" />
    <path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
  </Svg>
);
export const LockIcon = (p: IconProps & { open?: boolean }): JSX.Element => {
  const { open, ...rest } = p;
  return (
    <Svg {...rest}>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d={open ? 'M8 11V7a4 4 0 0 1 7.8-1.2' : 'M8 11V7a4 4 0 0 1 8 0v4'} />
    </Svg>
  );
};
export const CheckIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M20 6 9 17l-5-5" />
  </Svg>
);
export const CheckCircleIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.5 2.5 2.5 4.5-5" />
  </Svg>
);
export const ShareIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
    <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
  </Svg>
);
export const SendIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="m22 2-7 20-4-9-9-4z" />
    <path d="M22 2 11 13" />
  </Svg>
);
export const ExpandIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </Svg>
);
export const CollapseIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" />
  </Svg>
);
export const ChevronLeftIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="m15 18-6-6 6-6" />
  </Svg>
);
export const ChevronRightIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="m9 18 6-6-6-6" />
  </Svg>
);
export const ChevronDownIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
);
export const CloseIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Svg>
);
export const ChartIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M3 3v18h18" />
    <path d="M7 15v2M11 11v6M15 7v10M19 12v5" />
  </Svg>
);
export const LayersIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="m12 2 10 5-10 5L2 7z" />
    <path d="m2 17 10 5 10-5M2 12l10 5 10-5" />
  </Svg>
);
export const MapIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2z" />
    <path d="M9 4v14M15 6v14" />
  </Svg>
);
export const PenIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </Svg>
);
export const PolygonIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M5 7 12 3l7 5-2 10H7z" />
    <circle cx="5" cy="7" r="1.6" fill="currentColor" />
    <circle cx="12" cy="3" r="1.6" fill="currentColor" />
    <circle cx="19" cy="8" r="1.6" fill="currentColor" />
    <circle cx="17" cy="18" r="1.6" fill="currentColor" />
    <circle cx="7" cy="18" r="1.6" fill="currentColor" />
  </Svg>
);
export const SparkleIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.5 2.5M15.2 15.2l2.5 2.5M6.3 17.7l2.5-2.5M15.2 8.8l2.5-2.5" />
  </Svg>
);
export const TextIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M4 6h16M4 12h16M4 18h10" />
  </Svg>
);
export const PencilIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M16.9 3.6a2.1 2.1 0 0 1 3 3L8.4 18.1 4 19.5l1.4-4.4z" />
    <path d="m14.9 5.6 3 3" />
  </Svg>
);
export const TrashIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
  </Svg>
);
export const PlusIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);
export const UsersIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />
  </Svg>
);
export const SearchIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Svg>
);
export const CloudCheckIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M17.5 19a4.5 4.5 0 1 0-1.4-8.8A6 6 0 0 0 4.5 13 3.5 3.5 0 0 0 6.5 19z" />
    <path d="m9.5 14.5 2 2 3.5-3.5" />
  </Svg>
);
export const KeyboardIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M7 14h10" />
  </Svg>
);
export const AlertIcon = (p: IconProps): JSX.Element => (
  <Svg {...p}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4M12 17h.01" />
  </Svg>
);
